import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AdminUser, UserRole } from '../types';

interface UserTableProps {
  users: AdminUser[];
  currentPage: number;
  totalPages: number;
  totalElements: number;
  isLoading: boolean;
  isInitialLoading: boolean;
  error: string;
  canReadDetail: boolean;
  canUpdate: boolean;
  canUpdateStatus: boolean;
  pendingUserId: number | null;
  onPageChange: (page: number) => void;
  onView: (user: AdminUser) => void;
  onEdit: (user: AdminUser) => void;
  onToggleStatus: (user: AdminUser) => void;
  onRetry: () => void;
}

const ROLE_CLASSES: Record<UserRole, string> = {
  USER: 'bg-surface-container text-secondary',
  STAFF: 'bg-primary-fixed text-on-primary-fixed-variant',
  MANAGER: 'bg-tertiary-fixed text-on-tertiary-fixed-variant',
  ADMIN: 'bg-success-container text-on-success-container',
};

const UserAvatar: React.FC<{ user: AdminUser }> = ({ user }) => {
  const [hasImageError, setHasImageError] = useState(false);
  const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
  const fallback = (fullName || user.username || user.email).charAt(0).toUpperCase();

  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full border border-outline-variant bg-primary-fixed font-label-md text-primary">
      {user.avatarUrl && !hasImageError ? (
        <img
          className="h-full w-full object-cover"
          src={user.avatarUrl}
          alt=""
          onError={() => setHasImageError(true)}
        />
      ) : fallback}
    </div>
  );
};

const UserTable: React.FC<UserTableProps> = ({
  users,
  currentPage,
  totalPages,
  totalElements,
  isLoading,
  isInitialLoading,
  error,
  canReadDetail,
  canUpdate,
  canUpdateStatus,
  pendingUserId,
  onPageChange,
  onView,
  onEdit,
  onToggleStatus,
  onRetry,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith('vi') ? 'vi-VN' : 'en-US';

  const formatCreatedAt = (value: string) => {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '—' : new Intl.DateTimeFormat(locale).format(date);
  };

  return (
    <div className="relative flex min-h-[360px] flex-1 flex-col">
      {isLoading && !isInitialLoading && (
        <div className="absolute inset-x-0 top-0 z-20 h-0.5 overflow-hidden bg-primary-fixed" role="status" aria-label={t('admin.users.loading')}>
          <div className="h-full w-1/2 animate-pulse bg-primary" />
        </div>
      )}

      <div className="flex-1 overflow-x-auto">
        <table className={`w-full min-w-[920px] border-collapse text-left text-body-sm transition-opacity ${isLoading && !isInitialLoading ? 'opacity-60' : ''}`}>
          <thead className="bg-surface-container-low">
            <tr className="border-b border-outline-variant">
              {['user', 'role', 'status', 'contact', 'created_at', 'actions'].map((column) => (
                <th
                  key={column}
                  className={`px-5 py-3 font-label-sm text-label-sm text-secondary ${column === 'actions' ? 'text-right' : ''}`}
                >
                  {t(`admin.users.table.${column}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-outline-variant/60 bg-white">
            {isInitialLoading && Array.from({ length: 6 }, (_, index) => (
              <tr key={index}>
                <td colSpan={6} className="px-5 py-3">
                  <div className="h-10 animate-pulse rounded-lg bg-surface-container-low" />
                </td>
              </tr>
            ))}
            {!isInitialLoading && error && users.length === 0 && (
              <tr>
                <td colSpan={6} className="px-5 py-14 text-center">
                  <span className="material-symbols-outlined text-4xl text-error">cloud_off</span>
                  <p className="mt-2 font-medium text-on-surface">{t('admin.users.error.title')}</p>
                  <p className="mt-1 text-error">{error}</p>
                  <button type="button" className="mt-3 rounded-lg bg-primary px-4 py-2 font-label-md text-on-primary" onClick={onRetry}>
                    {t('admin.users.actions.retry')}
                  </button>
                </td>
              </tr>
            )}
            {!isInitialLoading && !error && users.length === 0 && (
              <tr>
                <td colSpan={6} className="px-5 py-14 text-center text-secondary">
                  <span className="material-symbols-outlined text-4xl text-outline">person_search</span>
                  <p className="mt-2 font-medium text-on-surface">{t('admin.users.empty.title')}</p>
                  <p className="mt-1">{t('admin.users.empty.description')}</p>
                </td>
              </tr>
            )}
            {!isInitialLoading && users.map((user) => {
              const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
              const isPending = pendingUserId === user.id;
              return (
                <tr
                  key={user.id}
                  className={`${canReadDetail ? 'cursor-pointer hover:bg-surface-container-lowest' : ''} ${isPending ? 'opacity-60' : ''}`}
                  onClick={() => canReadDetail && onView(user)}
                >
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <UserAvatar user={user} />
                      <div className="min-w-0">
                        <p className="max-w-[260px] truncate font-medium text-on-surface">{fullName || user.username}</p>
                        <p className="max-w-[260px] truncate text-[12px] text-secondary">@{user.username} · {user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${ROLE_CLASSES[user.role]}`}>
                      {t(`admin.users.roles.${user.role.toLowerCase()}`)}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${user.active ? 'bg-success-container text-on-success-container' : 'bg-surface-container text-secondary'}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${user.active ? 'bg-success' : 'bg-outline'}`} />
                      {t(`admin.users.status.${user.active ? 'active' : 'inactive'}`)}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-secondary">
                    <p>{user.phone || '—'}</p>
                    <p className="mt-0.5 max-w-[220px] truncate text-[12px]">{user.address || '—'}</p>
                  </td>
                  <td className="px-5 py-3 text-secondary">{formatCreatedAt(user.createdAt)}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {canUpdate && (
                        <button
                          type="button"
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-secondary hover:bg-primary-fixed hover:text-primary disabled:opacity-50"
                          disabled={isPending}
                          aria-label={t('admin.users.actions.edit_label', { user: user.username })}
                          onClick={(event) => {
                            event.stopPropagation();
                            onEdit(user);
                          }}
                        >
                          <span className="material-symbols-outlined text-[19px]">edit</span>
                        </button>
                      )}
                      {canUpdateStatus && (
                        <button
                          type="button"
                          className={`inline-flex h-9 w-9 items-center justify-center rounded-lg disabled:opacity-50 ${user.active ? 'text-error hover:bg-error-container/50' : 'text-primary hover:bg-primary-fixed'}`}
                          disabled={isPending}
                          aria-label={t(user.active ? 'admin.users.actions.deactivate_label' : 'admin.users.actions.activate_label', { user: user.username })}
                          onClick={(event) => {
                            event.stopPropagation();
                            onToggleStatus(user);
                          }}
                        >
                          <span className="material-symbols-outlined text-[19px]">{user.active ? 'person_off' : 'how_to_reg'}</span>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <footer className="flex flex-col items-center justify-between gap-3 border-t border-outline-variant bg-white px-4 py-3 text-center sm:flex-row sm:text-left">
        <p className="text-body-sm text-secondary">
          {t('admin.users.pagination.summary', { total: totalElements })}
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-outline-variant text-secondary disabled:opacity-40"
            disabled={currentPage <= 1 || isLoading}
            aria-label={t('admin.users.pagination.previous')}
            onClick={() => onPageChange(currentPage - 1)}
          >
            <span className="material-symbols-outlined">chevron_left</span>
          </button>
          <span className="min-w-20 text-center font-label-md text-on-surface">
            {t('admin.users.pagination.page', { current: currentPage, total: Math.max(totalPages, 1) })}
          </span>
          <button
            type="button"
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-outline-variant text-secondary disabled:opacity-40"
            disabled={currentPage >= totalPages || isLoading}
            aria-label={t('admin.users.pagination.next')}
            onClick={() => onPageChange(currentPage + 1)}
          >
            <span className="material-symbols-outlined">chevron_right</span>
          </button>
        </div>
      </footer>
    </div>
  );
};

export default UserTable;
